<template>
  <MainLayout section="guides">
    <LightBox :items="images" :index="imageIndex" />

    <b-row class="guide_content">
      <b-col>
        <b-row>
          <b-col>
            <h1><router-link to="/guides">Guides</router-link> / Trading and Finance</h1>
          </b-col>
        </b-row>

        <b-row>
          <b-col>
            <p>
            In this guide we'll go over pulling target metrics from a Distributed Ledger using Block Trac.
            </p>

            <center>
              <iframe :width="mq_lte_sm ? 300 : 560" :height="mq_lte_sm ? 170 : 315" src="https://www.youtube.com/embed/9PPmSVSpV2E" title="Block Trac - Trading and Finance" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen></iframe>
            </center>
          </b-col>
        </b-row>

        <b-row class="guide_section">
          <b-col>
            <h2>Setup</h2>
            <p>It is assumed that you are logged into the <router-link to="/txs">Block Trac</router-link> website and are familiar with creating a filter.</p>

            <p>If not please refer to previous <router-link to="/guides">guides</router-link> for step by step instructions.</p>

            <p>Today we'll be setting up filters used to match financial information that you are interested in and forward it to your system via webhooks.</p>

            <b-alert show variant="warning">
              <img :src="require('@/assets/exclamation.svg')" width="25" />
              <span>We'll be demonstrating basic examples which uses Block Trac notifcations, expand upon these to implement logic in accordance to your requirements.</span>
            </b-alert>
          </b-col>
        </b-row>

        <b-row class="guide_section">
          <b-col>
            <h2>Create a Basic Webhook</h2>

            <p>WebHooks may be implemented using any HTTP server technology. A simple example uses <b>nodejs</b> and the <b>expressjs</b> library.</p>

            <p>With an editor create app.js, containing:</p>

            <pre v-highlightjs>
            <code>
const express = require('express')
const app = express()
const port = 3000

// Webhook implementation, handle Block Trac notification
app.post('/', (req, res) => {
  // Do something with req.body.transactions
})

app.listen(port)
            </code>
            </pre>

            <p>And from the command line, run:</p>

            <pre v-highlightjs>
            <code>
$ yarn add express
$ node app.js
            </code>
            </pre>

            <p>Use a service such as <a href="https://ngrok.com">ngrok</a> to generate a URI for your local webhook to use with Block Trac.</p>
          </b-col>
        </b-row>

        <b-row class="guide_section">
          <b-col>
            <h2>Choose your Filter Pattern</h2>

            <p>The next step is to identify the type of activity that you are interested in.</p>
            <p>Explore the different Filter Categories that Block Trac provides, some of interest for financial applications include:</p>

            <ul>
              <li>Offer Create, Update, Destroy - Modify decentralized exchange order books</li>
              <li>Issued asset movement - Money flow and volume indicators</li>
              <li>Cross chain trends - For arbitrage and modeling</li>
            </ul>
          </b-col>
        </b-row>

        <b-row :style="mq_lte_sm ? {'flex-direction': 'column'} : {'align-items' : 'baseline'}">
          <b-col>
            <img :src="require('@/assets/guides/trading/xlm-trading-pair.png')" @click="set_image" />
          </b-col>
          <b-col>
            <img :src="require('@/assets/guides/trading/xrp-issued-asset-movement.png')" @click="set_image" />
          </b-col>
          <b-col>
            <img :src="require('@/assets/guides/trading/usd-xrp.png')" @click="set_image" />
          </b-col>
          <b-col>
            <img :src="require('@/assets/guides/trading/usd-xlm.png')" @click="set_image" />
          </b-col>
        </b-row>

        <b-row style="text-align: center" :style="mq_lte_sm ? {'flex-direction' : 'column'} : null">
          <b-col>
            Monitoring USDC/EURT offsets
          </b-col>

          <b-col>
            Monitoring CSC issued asset movement
          </b-col>

          <b-col>
            Monitoring USD/XRP trading for arbitrage
          </b-col>

          <b-col>
            Monitoring USD/XLM trading for arbitrage
          </b-col>
        </b-row>


        <b-row class="guide_subsection">
          <b-col>
            <p>Use the Block Trac filter categories and/or expression templates in conjunction with your parameters and logic to hone in on exactly what you are interested in.</p>
          </b-col>
        </b-row>

        <b-row class="guide_section">
          <b-col>
            <h2>Money flow example</h2>

            <p>Setup a filter and chose the category <b>Issued Asset Movement</b></p>

            <p>Specify the issued asset and issuer</p>

            <p>Specify the URI of your webhook</p>

            <p>In your webhook logic, store aggregate money flow and compute technical indicators</p>

            <pre v-highlightjs>
            <code>
let volume_by_currency = {}
let obv;
app.post('/', (req, res) => {
  // Iterate over transactions
  req.body.transactions.forEach((tx) => {

    // Derive currency and amount from blockchain transaction, eg...
    const currency_amount =
      tx.meta.AffectedNodes.find(n => n.LedgerEntryType == 'RippleState').FinalFields.Balance;
      // ...for xrp, or for xlm:
      tx.envelope.tx.operations.find(op => op._type == 'Payment').amount;

    // Update volume by currency
    volume_by_currency[currency_amount.currency] ||= 0
    volume_by_currency[currency_amount.currency] += currency_amount.amount
  })

  // Compute on-balance-volume technical indicator
  obv = indicators.OBV(volume_by_currency);
})
            </code>
            </pre>

            <p>From here data obtained from technical indicators, such as <b>OBV</b> above can be fed into automated trading and backtesting systems.</p>

            <img :src="require('@/assets/guides/trading/obv.png')" @click="set_image" :class="mq_gt_sm ? 'half_width' : null" />
          </b-col>
        </b-row>

        <b-row class="guide_section">
          <b-col>
            <h2>Arbitrage Example</h2>

            <p>Setting up a system to leverage on-ledger arbitrage opportunities is a matter of setting up two different filters for different chains.</p>

            <p>To start off, identify the two currencies that you wish to arbitrage, they can be the native assets of the distributed systems themselves or any issued currency.</p>

            <p>Pick a third "reference" currency with sufficient liquidity and setup two filters, one for each DLT, alerting your webhook as order books are updated.</p>

            <p>On your server, record offers received from Block Trac in conjuction with price reference points obtained by querying reputable exchanges.</p>

            <p>Look for arbitrage opportunities, when inverse rates yield profitable trading marging and indicators cross signal lines</p>

            <b-alert show variant="success">
              <img :src="require('@/assets/computer-happy.svg')" width="35" />
              <span>Pro Tip: This is a great integration point for Machine Learning and AI !</span>
            </b-alert>

            <img :src="require('@/assets/guides/trading/arbitrage.png')" @click="set_image" :class="mq_gt_sm ? 'half_width' : null" />
          </b-col>
        </b-row>

        <b-row class="guide_section">
          <b-col>
            <h2>Next Steps</h2>

            <p>We explored just a few ways which the tier-1 blockchain data that Block Trac affords can be integrated into your trading and financial system.</p>
            <p>Now go ahead and build the bots and models of tommorow!</p>
            <p>Return to <router-link to="/guides">Guides</router-link> to learn about more types of DLT activity that you can monitor.</p>
          </b-col>
        </b-row>
      </b-col>
    </b-row>
  </MainLayout>
</template>

<script>
  import MainLayout from '../components/MainLayout'
  import LightBox from '../components/LightBox'
  import Blockchain from '../mixins/blockchain'

  export default{
    mixins : [Blockchain],

    components: {
      MainLayout,
      LightBox
    },

    data (){
      return {
        imageIndex : null,
        images : []
      }
    },

    methods : {
      set_image (evnt){
        this.images = [evnt.target.src];
        this.imageIndex = 0;
      }
    },

    mounted (){
      this.images = [];
      this.imageIndex = null;
    }
  }
</script>
